import {CallEventType} from '@/App/RealtimeProvider';
import {stateMap} from '@amplify/athena-models';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import {useState} from 'react';
import {useStyles} from './LiveCallComponents.styles';
import {formatDate, formatCurrency, formatArray} from './utils/LiveCallUtils';
import {BadgeCheck} from 'lucide-react';
import startCase from 'lodash/startCase';

// Interfaces
export interface LiveCallComponentsProps {
    currentCall: CallEventType;
    onNewForm: () => void;
    onHubspot: () => void;
    getUserData: (currentCall: any, fieldName: string, fallback?: any) => any;
    connectionStatus: string;
    subscriptionStatus: string;
    showRefreshButton: boolean;
    handleRefresh: () => void;
    onClearCall?: () => void;
}

export interface CallComponentProps {
    currentCall: CallEventType;
    onNewForm: () => void;
    getUserData: (currentCall: any, fieldName: string, fallback?: any) => any;
    connectionStatus: string;
    subscriptionStatus: string;
    showRefreshButton: boolean;
    handleRefresh: () => void;
    onClearCall?: () => void;
    tooltipText: string | undefined;
}

// Helper component for the client header
export const ClientHeader = ({
    clientName,
    onViewContact,
    onAddCase,
    classes,
    chipLabel,
}: {
    clientName: string;
    onViewContact?: () => void;
    onAddCase: () => void;
    chipLabel: string | undefined;
    classes: any;
}) => (
    <Grid container justify="space-between" alignItems="center" className={classes.header}>
        <Grid item>
            <h1 className={classes.clientName}>{clientName}</h1>
        </Grid>
        <Grid item>
            <Grid container spacing={1} alignItems="center">
                {chipLabel && chipLabel !== 'Untagged' && (
                    <Grid item>
                        <Chip
                            label={chipLabel}
                            style={{backgroundColor: '#E8FFEF', color: '#178F3F', fontWeight: 700}}
                        />
                    </Grid>
                )}
                <Grid item>
                    <Button className={classes.viewContactButton} onClick={onViewContact}>
                        VIEW CONTACT
                    </Button>
                </Grid>
                <Grid item>
                    <Button className={classes.addCaseButton} onClick={onAddCase}>
                        + ADD NEW CASE
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    </Grid>
);

// Helper component for field display
export const FieldGroup = ({label, value, classes}: {label: string; value: React.ReactNode; classes: any}) => (
    <div className={classes.fieldGroup}>
        <div className={classes.fieldLabel}>{label}</div>
        <div className={classes.fieldValue}>
            {value === null || value === undefined || value === '' ? 'N/A' : value}
        </div>
    </div>
);

export const StatusIndicator = ({label, status, className}: {label: string; status: string; className?: string}) => {
    const getStatusColor = () => {
        switch (status.toLowerCase()) {
            case 'connected':
            case 'subscribed':
                return '#4CAF50';
            case 'connecting':
            case 'reconnecting':
            case 'subscribing':
                return '#FFA726';
            case 'error':
            default:
                return '#F44336';
        }
    };

    return (
        <Box display="flex" alignItems="center" marginRight={2} className={className} style={{fontSize: '14px'}}>
            <Box
                component="span"
                marginRight={1}
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(),
                    display: 'inline-block',
                }}
            />
            {label}: {status}
        </Box>
    );
};

export const BasicStatusIndicators = ({
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
}: {
    connectionStatus: string;
    subscriptionStatus: string;
    showRefreshButton: boolean;
    handleRefresh: () => void;
}) => {
    const classes = useStyles();

    return (
        <div className={classes.statusContainer}>
            <StatusIndicator label="Connection" status={connectionStatus} />
            <StatusIndicator label="Subscription" status={subscriptionStatus} />

            {showRefreshButton && (
                <IconButton
                    size="small"
                    onClick={handleRefresh}
                    style={{
                        marginLeft: 8,
                        padding: 4,
                        color: '#666',
                    }}
                    title="Refresh page"
                >
                    <RefreshIcon style={{fontSize: 16}} />
                </IconButton>
            )}
        </div>
    );
};

export const StatusIndicatorsSection = ({
    connectionStatus,
    subscriptionStatus,
    showRefreshButton,
    handleRefresh,
    onClearCall,
}: {
    connectionStatus: string;
    subscriptionStatus: string;
    showRefreshButton: boolean;
    handleRefresh: () => void;
    onClearCall?: () => void;
}) => {
    const classes = useStyles();
    const [clearCallDialogOpen, setClearCallDialogOpen] = useState(false);

    const handleClearCall = () => {
        setClearCallDialogOpen(true);
    };

    const handleClearCallConfirm = () => {
        setClearCallDialogOpen(false);
        onClearCall?.();
    };

    const handleClearCallCancel = () => {
        setClearCallDialogOpen(false);
    };

    return (
        <>
            <div className={classes.statusContainer}>
                <StatusIndicator label="Connection" status={connectionStatus} />
                <StatusIndicator label="Subscription" status={subscriptionStatus} />
                {showRefreshButton && (
                    <IconButton
                        size="small"
                        onClick={handleRefresh}
                        style={{
                            marginLeft: 8,
                            padding: 4,
                            color: '#666',
                        }}
                        title="Refresh page"
                    >
                        <RefreshIcon style={{fontSize: 16}} />
                    </IconButton>
                )}
                {onClearCall && (
                    <Button
                        size="small"
                        onClick={handleClearCall}
                        style={{
                            marginLeft: 16,
                            padding: '2px 8px',
                            fontSize: '12px',
                            color: '#666',
                            textTransform: 'none',
                        }}
                        title="Clear call and return to waiting"
                    >
                        Clear Call
                    </Button>
                )}
            </div>

            <Dialog open={clearCallDialogOpen} onClose={handleClearCallCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Clear Call</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to clear this call? This will return you to the waiting screen.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClearCallCancel}
                        style={{
                            backgroundColor: 'white',
                            color: '#5865f2',
                            border: '1px solid #5865f2',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleClearCallConfirm}
                        style={{
                            backgroundColor: '#5865f2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                        }}
                    >
                        Clear Call
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const ClientInfoSection = ({
    currentCall,
    getUserData,
    onNewForm,
    variant = 'dialing',
    tooltipText,
}: {
    currentCall: CallEventType;
    getUserData: (currentCall: any, fieldName: string, fallback?: any) => any;
    onNewForm: () => void;
    tooltipText: string | undefined;
    variant?: 'dialing' | 'connected';
}) => {
    const classes = useStyles();
    const clientName = `${getUserData(currentCall, 'firstName')} ${getUserData(currentCall, 'lastName')}`;

    const handleViewContact = () => {
        window.open(currentCall.crmLink, '_blank', 'noopener,noreferrer');
    };

    const getOpportunityTypeValue = () => {
        return variant === 'connected'
            ? currentCall.hubspotData?.recommended_product_name?.value || 'N/A'
            : getUserData(currentCall, 'productTypeRecommendation');
    };

    return (
        <div className={classes.contentCard}>
            <ClientHeader
                clientName={clientName}
                chipLabel={currentCall.persona ? currentCall.persona : undefined}
                onViewContact={handleViewContact}
                onAddCase={onNewForm}
                classes={classes}
            />
            {tooltipText && (
                <div className={classes.insightTooltip}>
                    <span className={classes.insightTooltipLabel}>
                        <BadgeCheck />
                    </span>
                    <span className={classes.insightTooltipTextItalic}>Persona Tip: </span>{' '}
                    <span className={classes.insightTooltipText}>{tooltipText}</span>
                </div>
            )}

            <div className={classes.section}>
                <div className={classes.sectionTitle}>Who You're Calling</div>
                <div className={classes.fieldGrid}>
                    <FieldGroup
                        label="First Name"
                        value={startCase(getUserData(currentCall, 'firstName') || '')}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Last Name"
                        value={startCase(getUserData(currentCall, 'lastName') || '')}
                        classes={classes}
                    />
                    <FieldGroup label="Phone" value={currentCall.clientPhoneNumber} classes={classes} />
                    <FieldGroup label="Email" value={currentCall.clientEmail} classes={classes} />
                    <FieldGroup
                        label="Occupation"
                        value={startCase(getUserData(currentCall, 'occupation') || '')}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Date of Birth"
                        value={formatDate(getUserData(currentCall, 'date_of_birth'))}
                        classes={classes}
                    />
                    <FieldGroup
                        label="State"
                        value={stateMap.get(getUserData(currentCall, 'state')) || 'Unknown'}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Income"
                        value={formatCurrency(getUserData(currentCall, 'annualHouseholdIncomeNumber'))}
                        classes={classes}
                    />
                </div>
                <div className={classes.fieldGrid}>
                    <FieldGroup
                        label="Marital Status"
                        value={getUserData(currentCall, 'maritalStatus')}
                        classes={classes}
                    />
                    <FieldGroup label="Kids" value={getUserData(currentCall, 'numberOfChildren')} classes={classes} />
                    <FieldGroup
                        label="Existing Coverage"
                        value={getUserData(currentCall, 'existingCoverage', 'N/A')}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Original Source"
                        value={currentCall.hubspotData?.hs_analytics_source?.value || 'N/A'}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Original Source Drill Down"
                        value={currentCall.hubspotData?.hs_analytics_source_data_1?.value || 'N/A'}
                        classes={classes}
                    />
                </div>
                <div className={classes.fieldGrid}>
                    <FieldGroup
                        label="Walkthrough Completed Time"
                        value={formatDate(getUserData(currentCall, 'updated_at'), true)}
                        classes={classes}
                    />
                </div>
            </div>

            <div className={classes.section}>
                <div className={classes.sectionTitle}>Why You're Calling</div>
                <div className={classes.fieldGrid}>
                    <FieldGroup label="Opportunity Type" value={getOpportunityTypeValue()} classes={classes} />
                    <FieldGroup
                        label="Goal"
                        value={startCase(formatArray(getUserData(currentCall, 'investmentGoals', [])) || '')}
                        classes={classes}
                    />
                    <FieldGroup
                        label="Selected Monthly Contribution"
                        value={getUserData(currentCall, 'modalPremium')}
                        classes={classes}
                    />
                </div>
            </div>
        </div>
    );
};
